import React from 'react';
import { useFloating, useMergeRefs, autoUpdate, offset, shift } from '@floating-ui/react';
import { ChevronDownIcon, XIcon } from '../icons';
import {
  PillWrapper, PillButton, PillLabel, PillValue, PillClear, PillPopover,
  PillSearchInput, PillOptionList, PillOption, PillEmpty, PillDivider,
} from '../inventory.styles';

// Pigułka filtra magazynu (redesign 2026-09-22).
//
// Zastąpiła `FilterDropdown` używany wewnątrz modala filtrów. Różnica nie jest kosmetyczna:
// tamten komponent w połowie składał się z obsługi modala (`filterPopupRef`,
// `setExtraBottomPadding`, mierzenie, czy lista opcji nie wyjdzie poza panel, wyszukiwanie
// `[data-filter-popup]` w całym dokumencie). Bez modala to wszystko jest zbędne, więc
// zostało samo sedno: przycisk, który POKAZUJE swój stan, i lista wyboru pod nim.
//
// Pigułka jest jednocześnie kontrolką i podsumowaniem — ustawiona nosi swoją wartość
// („Stan: Dostępny") i krzyżyk do zdjęcia. Dzięki temu pasek filtrów da się przeczytać
// jak zdanie, bez otwierania czegokolwiek; wcześniej jedynym śladem po ustawionych
// filtrach była liczba w odznace przy przycisku „Filtry".
export default function FilterPill({
  label,
  value,             // tekst do pokazania na pigułce, albo null gdy filtr nieaktywny
  options,           // [{value, label}] — gdy pusto, renderują się same `children`
  onSelect,
  onClear,
  children,          // dodatkowa kontrolka w dymku (np. zakres rozmiaru)
  optionsLabel,      // podpis listy wartości, gdy nad nią stoi `children` (np. „albo wybierz rozmiar”)
  searchPlaceholder,
  emptyLabel,
  clearLabel,
  showSearch = true,
  'data-cy': dataCy,
}) {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const wrapperRef = React.useRef(null);
  const searchRef = React.useRef(null);

  // Położenie dymku liczy @floating-ui/react. shift dosuwa dymek do krawędzi obszaru, który go
  // przycina (PanelContainer z overflow hidden, a nie samo okno), a autoUpdate przelicza je, gdy
  // pigułka urośnie albo przeskoczy do kolejnego rzędu przy otwartym dymku. Dawny własny pomiar
  // liczył tylko przy otwarciu i względem okna, więc dymek wyjeżdżał za lewą krawędź ekranu
  // albo był przycinany przez panel (przegląd 28.09.2026).
  const { refs, floatingStyles } = useFloating({
    open,
    placement: 'bottom-start',
    middleware: [offset(6), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate,
  });
  const wrapperRefy = useMergeRefs([wrapperRef, refs.setReference]);

  const lista = Array.isArray(options) ? options : [];
  // Pozycja "wyczyść" z listy opcji jest zbędna, odkąd pigułka ma własny krzyżyk —
  // zostawiona dublowałaby akcję i zajmowała pierwszy wiersz każdego dymku.
  const wybieralne = lista.filter((o) => o && String(o.value ?? '').trim() !== '');

  const widoczne = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return wybieralne;
    return wybieralne.filter((o) => String(o.label ?? o.value).toLowerCase().includes(q));
  }, [wybieralne, query]);

  // Lista wyszukiwarki ma sens dopiero, gdy jest w czym szukać.
  const zWyszukiwarka = showSearch && wybieralne.length > 8;

  React.useEffect(() => {
    if (!open) return undefined;
    const naZewnatrz = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    const naEscape = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', naZewnatrz);
    document.addEventListener('keydown', naEscape);
    return () => {
      document.removeEventListener('mousedown', naZewnatrz);
      document.removeEventListener('keydown', naEscape);
    };
  }, [open]);

  React.useEffect(() => {
    if (!open) { setQuery(''); return; }
    if (zWyszukiwarka && searchRef.current) searchRef.current.focus();
  }, [open, zWyszukiwarka]);

  const aktywny = value != null && String(value).trim() !== '';

  return (
    <PillWrapper ref={wrapperRefy}>
      <PillButton
        type="button"
        data-cy={dataCy}
        $active={aktywny}
        aria-expanded={open}
        aria-haspopup="listbox"
        onClick={() => setOpen((v) => !v)}
      >
        <PillLabel>{label}</PillLabel>
        {aktywny && <PillValue>{value}</PillValue>}
        <ChevronDownIcon />
      </PillButton>

      {/* Krzyżyk stoi OBOK przycisku, nie w nim: zagnieżdżony <button> w <button> jest
          nieprawidłowym HTML-em i przeglądarka gubi wtedy kliknięcia. */}
      {aktywny && onClear && (
        <PillClear
          type="button"
          onClick={() => { onClear(); setOpen(false); }}
          aria-label={clearLabel}
          title={clearLabel}
        >
          <XIcon />
        </PillClear>
      )}

      {open && (
        <PillPopover ref={refs.setFloating} role="listbox" aria-label={label} style={floatingStyles}>
          {children}
          {wybieralne.length > 0 && (
            <>
              {children && optionsLabel && <PillDivider>{optionsLabel}</PillDivider>}
              {zWyszukiwarka && (
                <PillSearchInput
                  ref={searchRef}
                  value={query}
                  placeholder={searchPlaceholder}
                  onChange={(e) => setQuery(e.target.value)}
                />
              )}
              <PillOptionList>
                {widoczne.map((o) => (
                  <PillOption
                    key={o.value}
                    type="button"
                    role="option"
                    aria-selected={String(o.value) === String(value)}
                    $selected={String(o.value) === String(value)}
                    onClick={() => { onSelect(o.value); setOpen(false); }}
                  >
                    {o.label ?? o.value}
                  </PillOption>
                ))}
                {widoczne.length === 0 && <PillEmpty>{emptyLabel}</PillEmpty>}
              </PillOptionList>
            </>
          )}
        </PillPopover>
      )}
    </PillWrapper>
  );
}
